import { TRPCClientError } from '@trpc/client';

import { trpcFehlerCode } from '../trpc';

/**
 * Was einem Aufruf zugestossen ist — und was die Oberflaeche daraufhin anbieten
 * darf (AC-016, AK-4).
 *
 * AK-4 verlangt eine KONKRETE Meldung, nicht „Ein Fehler ist aufgetreten".
 * Konkret heisst hier: unterscheidbar in dem, was der Anwender als Naechstes
 * tun kann. Genau sechs Wege gibt es, und die acht Arten unten verteilen
 * sich darauf:
 *
 *   erneut versuchen        -> `netz`, `server`
 *   neu laden, dann erneut  -> `konflikt`
 *   neu anmelden            -> `sitzung`
 *   Eingabe korrigieren     -> `eingabe`
 *   nichts hilft            -> `zugriff`, `nicht_gefunden`
 *   GAR NICHTS anzeigen     -> `abgebrochen`
 *
 * Eine feinere Unterteilung waere keine bessere Auskunft, sondern eine, die
 * niemand in eine Handlung uebersetzen kann. `abgebrochen` kam mit dem
 * KI-Agent-Review dazu und ist genau deshalb eine eigene Art: „nichts
 * anzeigen" ist ein Weg, den die anderen fuenf nicht abdecken — ohne ihn
 * meldete die Anwendung einen Fehler fuer etwas, das sie selbst veranlasst
 * hat.
 */
export type FehlerArt =
  /** Die Anfrage hat den Server nicht erreicht oder die Antwort war unlesbar. */
  | 'netz'
  /** Die Sitzung fehlt oder ist abgelaufen (401). */
  | 'sitzung'
  /** Der Server hat abgelehnt, obwohl ein Zugang vorlag (403). */
  | 'zugriff'
  /** Die Eingabe hat die Pruefung nicht bestanden (400). */
  | 'eingabe'
  /** Der angefragte Datensatz existiert nicht oder gehoert zu einem anderen Mandanten (404). */
  | 'nicht_gefunden'
  /**
   * Der Stand hat sich geaendert oder wird gerade geschrieben (409, seit
   * AC-505): die Zuordnung eines PACS-Schritts stand nicht mehr so, wie der
   * Aufrufer sie gesehen hat, oder ein PACS-Abgleich hielt die Mandantensperre.
   *
   * Eigene Art und NICHT `server`: Die API wirft CONFLICT ausschliesslich,
   * bevor etwas committet ist — die Transaktion wird dabei zurueckgerollt. Als
   * `server` bekaeme der Anwender ausgerechnet hier die Warnung „koennte
   * bereits gespeichert sein", fuer einen Vorgang, der nachweislich nichts
   * geschrieben hat. Wer am Server CONFLICT wirft, haelt diese Zusage ein.
   */
  | 'konflikt'
  /** Alles Uebrige: ein Fehler auf der Serverseite. */
  | 'server'
  /**
   * Die Anwendung hat den Aufruf SELBST abgebrochen (Ansicht verlassen,
   * Eingabe ueberholt). Bei einer ABFRAGE zeigt die Oberflaeche dazu nichts an
   * — es ist kein Fehler, sondern eine Absicht.
   *
   * BEI EINEM SCHREIBZUGRIFF GILT DAS NICHT. Ein Abbruch stoppt das Warten,
   * nicht den Server: Die Buchung kann laengst geschrieben sein. Dann steht
   * `moeglicherweiseAusgefuehrt` auf `true`, und DAS muss die Oberflaeche
   * zeigen — sonst verschwindet ausgerechnet der Fall stillschweigend, in dem
   * ein append-only Eintrag (ADR-10) entstanden sein koennte, ohne dass jemand
   * davon weiss. (Befund aus dem KI-Agent-Review.)
   */
  | 'abgebrochen';

/** Ein Befund je Feld, aus `trpcErrorDataSchema.validation` der API. */
export interface FeldBefund {
  readonly path: string;
  readonly message: string;
}

export interface FehlerBefund {
  readonly art: FehlerArt;
  /** Fuer Menschen, konkret genug zum Handeln. */
  readonly meldung: string;
  /**
   * Darf die Oberflaeche „Erneut versuchen" anbieten?
   *
   * Gemeint ist die WIEDERHOLUNG DURCH DEN ANWENDER, nicht eine selbsttaetige.
   * Der Unterschied ist bei Schreibzugriffen wesentlich: ein automatischer
   * zweiter Versuch kann einen Vorgang doppelt ausfuehren, solange die
   * Prozeduren nicht nachweislich mehrfach ausfuehrbar sind.
   *
   * EINE SELBSTTAETIGE WIEDERHOLUNG GIBT ES IN DIESER ANWENDUNG NICHT, und
   * das ist eine Entscheidung: Sie braeuchte TanStack Query, und das ist
   * bewusst nicht installiert (CLAUDE.md §20.2, Punkt 11). Ein frueherer
   * Kommentar verwies hier auf eine Datei `query-client.ts`, die es nie gab.
   * Wer sie einfuehrt, entscheidet die Frage dort — und liest vorher
   * `moeglicherweiseAusgefuehrt`.
   *
   * EINZIGE AUSNAHME (AC-409): `daten/warteschlange.ts` wiederholt einen
   * VERSIONIERTEN Schreibzugriff nach Wiederverbindung. Das ist gefahrlos, weil
   * die Versionspruefung am Server ein zweites Wirken ausschliesst; fuer
   * unversionierte Schreibzugriffe gilt der Absatz oben unveraendert.
   */
  readonly wiederholbar: boolean;
  /**
   * Soll die Oberflaeche den Weg zur Neuanmeldung anbieten?
   *
   * Bei `zugriff` steht das auf `true`, obwohl 403 oft „dir fehlt ein Recht"
   * heisst. Der Grund steht in `trpc.ts`: der Server antwortet aus mehreren
   * Gruenden mit 403 — fehlendes Recht, abgeschalteter Mandant, nicht erlaubte
   * Herkunft — und unterscheidet sie ABSICHTLICH nicht, weil die Unterscheidung
   * ein Orakel waere. Einer dieser Gruende ist durch Neuanmeldung behebbar.
   * Deshalb anbieten und nicht erzwingen.
   */
  readonly neuAnmelden: boolean;
  /**
   * Koennte der Server den Vorgang TROTZ des Fehlers ausgefuehrt haben?
   *
   * Nur bei Schreibzugriffen `true` (siehe `KlassifikationsKontext`) und nur
   * bei den Arten, bei denen der Ausgang ungewiss ist. Bei `false` ist eine
   * Wiederholung gefahrlos.
   *
   * WARUM DAS EIN EIGENES FELD IST: `wiederholbar` allein sagt „erneut
   * versuchen ist sinnvoll" — es sagt NICHT, ob der erste Versuch vielleicht
   * schon gewirkt hat. Genau das ist bei einer verlorenen Antwort der Fall:
   * Die Buchung ist geschrieben, die Antwort ging unterwegs verloren. Ein
   * zweiter Versuch legt dann einen ZWEITEN Eintrag an — bei append-only
   * fortgeschriebenem Restaufwand (ADR-10) ist der nicht korrigierbar, und im
   * Audit-Log (AC-009) steht er unloeschbar daneben.
   *
   * Die Oberflaeche bietet die Wiederholung deshalb nicht wortgleich an: „Der
   * Vorgang koennte bereits gespeichert sein — bitte pruefen Sie den Stand,
   * bevor Sie es erneut versuchen." (Befund aus dem KI-Agent-Review.)
   */
  readonly moeglicherweiseAusgefuehrt: boolean;
  /** Feldbefunde, nur bei `eingabe`. Leer, wenn die API keine mitgeliefert hat. */
  readonly felder: readonly FeldBefund[];
}

export interface KlassifikationsKontext {
  /**
   * War der gescheiterte Aufruf ein SCHREIBZUGRIFF?
   *
   * Der Fehler allein verraet das nicht — der Klassifizierer sieht eine
   * Ablehnung, nicht die Prozedur.
   *
   * PFLICHTANGABE, und zwar aus demselben Grund, aus dem `meta.access` am
   * Server Pflicht ist (Regel 20): Ein optionales Feld mit sicher klingender
   * Voreinstellung ist ein Feld, das irgendwann jemand weglaesst. Hier waere
   * die Folge, dass eine Mutation nach einem Netzfehler eine schlichte
   * Wiederholung anbietet — und der zweite Versuch eine zweite, nicht
   * korrigierbare append-only Zeile anlegt (ADR-10), mit unloeschbarem
   * Audit-Eintrag daneben (AC-009).
   *
   * „Abfrage" und „vergessen" muessen im Code verschieden aussehen, und nur
   * eines davon darf durchgehen. `{ schreibzugriff: false }` ist deshalb eine
   * AUSSAGE und kein Weglassen — dieselbe Linie wie `ANY_AUTHENTICATED`.
   * (Befund aus dem KI-Agent-Review.)
   */
  readonly schreibzugriff: boolean;
}

/**
 * Die Meldungen stehen HIER und nicht am Server.
 *
 * Der Server formuliert bewusst generisch („Interner Fehler", „Nicht
 * angemeldet", „Kein Zugriff"): seine Antwort darf nicht verraten, woran es
 * lag, und sie wird an Stellen protokolliert, an die kein Fachtext gehoert
 * (`trpc.contract.ts`, `TrpcExceptionFilter`). Was der ANWENDER lesen soll, ist
 * eine Frage der Oberflaeche — und aendert sich, ohne dass der Vertrag bricht.
 *
 * Deshalb wird `error.message` ausdruecklich NICHT durchgereicht.
 */
const MELDUNGEN: Readonly<Record<FehlerArt, string>> = {
  netz: 'Der Server ist nicht erreichbar. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
  sitzung: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
  zugriff: 'Diese Funktion steht Ihnen nicht zur Verfügung.',
  eingabe: 'Die Eingabe ist unvollständig, ungültig oder zu umfangreich.',
  nicht_gefunden: 'Der Datensatz ist nicht mehr vorhanden. Bitte laden Sie die Ansicht neu.',
  konflikt:
    'Die Daten wurden inzwischen geändert oder werden gerade aktualisiert. Bitte laden Sie die Ansicht neu und versuchen Sie es dann erneut.',
  server: 'Auf dem Server ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
  // Wird im Regelfall NICHT angezeigt. Sie steht hier, weil jede Art eine
  // Meldung trägt und ein `undefined` an einer Anzeigestelle schlimmer wäre.
  abgebrochen: 'Der Vorgang wurde abgebrochen.',
};

/** Welcher tRPC-Code welche Art bedeutet. Alles Uebrige ist `server`. */
const ART_NACH_CODE: Readonly<Partial<Record<string, FehlerArt>>> = {
  UNAUTHORIZED: 'sitzung',
  FORBIDDEN: 'zugriff',
  BAD_REQUEST: 'eingabe',
  PARSE_ERROR: 'eingabe',
  NOT_FOUND: 'nicht_gefunden',
  CONFLICT: 'konflikt',
};

/** Welche Art eine Wiederholung durch den Anwender sinnvoll macht. */
const WIEDERHOLBAR: Readonly<Record<FehlerArt, boolean>> = {
  netz: true,
  server: true,
  // Nach dem Neuladen — die Meldung sagt das. Gefahrlos, weil der Server vor
  // dem Konflikt nichts geschrieben hat (siehe `AUSGANG_UNGEWISS`).
  konflikt: true,
  // Derselbe Aufruf mit denselben Daten scheitert erneut. Eine Schaltflaeche
  // „Erneut versuchen" waere hier eine Einladung zur Endlosschleife — und jeder
  // abgelehnte Versuch schreibt seit AC-007 eine UNLOESCHBARE Audit-Zeile
  // (CLAUDE.md §19). Eine Wiederholungsschleife auf einem 403 laesst die
  // Tabelle dauerhaft wachsen; das ist dort ausdruecklich als Risiko benannt.
  zugriff: false,
  eingabe: false,
  nicht_gefunden: false,
  // Erst nach der Neuanmeldung, und darum kuemmert sich `neuAnmelden`.
  sitzung: false,
  // Nichts anzubieten: Der Abbruch war die Absicht der Anwendung selbst.
  abgebrochen: false,
};

const NEU_ANMELDEN: Readonly<Record<FehlerArt, boolean>> = {
  sitzung: true,
  zugriff: true,
  netz: false,
  server: false,
  konflikt: false,
  eingabe: false,
  nicht_gefunden: false,
  abgebrochen: false,
};

/**
 * Die zwei Fälle, in denen der CLIENT scheitert, bevor etwas das Netz erreicht.
 *
 * Beide kommen als `TRPCClientError` OHNE `data` an und sähen damit aus wie ein
 * Netzfehler — gemessen am 23.09.2026 gegen `@trpc/client@11.18.0`. Die Folge
 * wäre in beiden Fällen dieselbe falsche Auskunft: „Der Server ist nicht
 * erreichbar", mit einer Schaltfläche „Erneut versuchen", die nie zum Ziel
 * führt. Beim Abbruch hätte die Anwendung dem Anwender sogar einen Fehler für
 * etwas gemeldet, das sie selbst veranlasst hat.
 */

/** Ein Abbruch trägt einen STANDARDISIERTEN Namen, keine Meldung. */
function istAbbruch(error: unknown): boolean {
  const ursache: unknown = (error as { cause?: unknown } | null)?.cause;

  // `AbortError` ist der Name aus dem DOM-Standard, den `AbortSignal` wirft.
  // Deshalb der Name und nicht die Meldung: Die Meldung lautet in Node „This
  // operation was aborted", im Browser anders — der Name ist überall gleich.
  return (
    typeof ursache === 'object' &&
    ursache !== null &&
    (ursache as { name?: unknown }).name === 'AbortError'
  );
}

/**
 * Der eine Fall, für den es KEINEN stabilen Erkennungspunkt gibt.
 *
 * `@trpc/client` wirft ihn als einfachen `Error` mit genau dieser Meldung
 * (`src/internals/dataLoader.ts`), wenn die Eingabe eines EINZELNEN Aufrufs
 * allein schon über `maxURLLength` liegt — dann gibt es nichts mehr
 * aufzuteilen, und der Aufruf verlässt den Client nie.
 *
 * AUF EINE MELDUNG ZU PRÜFEN IST SONST VERBOTEN in diesem Repository, und zwar
 * zu Recht: Meldungen sind für Menschen und dürfen sich ändern. Hier ist die
 * Abwägung eine andere, und deshalb steht sie ausgeschrieben da:
 *
 *   - Es ist KEINE Servermeldung, sondern eine Kennung aus einer Bibliothek,
 *     deren Version exakt gepinnt ist (11.18.0).
 *   - Die Alternative wäre, den Fall als Netzfehler mit endloser
 *     Wiederholung stehen zu lassen — schlechter.
 *   - Ein Test erzeugt ihn über den ECHTEN Client. Ändert eine künftige
 *     tRPC-Fassung die Kennung, wird er ROT, statt dass die Erkennung still
 *     ausfällt. Damit ist die Brüchigkeit bemerkbar gemacht statt hingenommen.
 */
const ZU_GROSSE_EINGABE = 'Input is too big for a single dispatch';

function istZuGross(error: unknown): boolean {
  return error instanceof Error && error.message === ZU_GROSSE_EINGABE;
}

/**
 * Bei welchen Arten der Ausgang am Server UNGEWISS ist.
 *
 * Die `false`-Faelle sind keine Vermutung, sondern folgen aus dem Aufbau
 * der API: 401 und 403 entstehen im `TenantGuard` beziehungsweise in der
 * RBAC-Middleware, und die prueft AUSDRUECKLICH VOR dem Datenzugriff (AC-007,
 * CLAUDE.md §20 und Regel 20). Ein Eingabefehler entsteht in der
 * Schema-Pruefung vor dem Resolver, und 404 heisst, dass nichts gefunden wurde.
 * Ein Konflikt (409) wird geworfen, bevor etwas committet ist, und rollt die
 * Transaktion zurueck (AC-505). In keinem dieser Faelle ist eine Zeile
 * geschrieben worden.
 *
 * `abgebrochen` steht dagegen auf `true`, und das ist der unintuitive Eintrag:
 * Ein Abbruch stoppt das WARTEN, nicht den Server. Die Anfrage kann laengst
 * angekommen und ausgefuehrt sein.
 */
const AUSGANG_UNGEWISS: Readonly<Record<FehlerArt, boolean>> = {
  // Keine oder eine unlesbare Antwort. Ob die Anfrage den Server erreicht hat,
  // ist von hier aus nicht feststellbar — bewusst konservativ.
  netz: true,
  // Ein 500 kann vor oder NACH dem Schreiben entstanden sein.
  server: true,
  abgebrochen: true,
  sitzung: false,
  zugriff: false,
  eingabe: false,
  nicht_gefunden: false,
  konflikt: false,
};

/** Liest die Feldbefunde aus `data.validation`, ohne der Form zu vertrauen. */
function feldBefunde(error: unknown): readonly FeldBefund[] {
  if (!(error instanceof TRPCClientError)) {
    return [];
  }

  const roh: unknown = (error.data as { validation?: unknown } | undefined)?.validation;

  if (!Array.isArray(roh)) {
    return [];
  }

  // Jeder Eintrag wird einzeln geprueft statt der ganzen Liste vertraut: was
  // hier ankommt, ist eine Antwort aus dem Netz. Ein unbrauchbarer Eintrag wird
  // uebergangen und reisst nicht die Fehleranzeige mit.
  return roh.flatMap((eintrag: unknown): FeldBefund[] => {
    if (typeof eintrag !== 'object' || eintrag === null) {
      return [];
    }

    const { path, message } = eintrag as { path?: unknown; message?: unknown };

    return typeof path === 'string' && typeof message === 'string' ? [{ path, message }] : [];
  });
}

/**
 * Uebersetzt einen beliebigen Fehler in einen Befund, den die Oberflaeche
 * anzeigen kann.
 *
 * Nimmt `unknown` und wirft nie. Eine Fehlerbehandlung, die selbst scheitern
 * kann, ist keine — und im `catch` steht in TypeScript grundsaetzlich `unknown`.
 */
function bestimmeArt(error: unknown, code: string | null, bekannt: boolean): FehlerArt {
  if (code !== null) {
    return bekannt ? (ART_NACH_CODE[code] ?? 'server') : 'server';
  }

  // Die beiden clientseitigen Fälle VOR dem Netzfall: Ohne sie wäre jeder
  // abgebrochene Aufruf ein „Server nicht erreichbar" mit Wiederholung.
  if (istAbbruch(error)) {
    return 'abgebrochen';
  }

  if (istZuGross(error)) {
    return 'eingabe';
  }

  return 'netz';
}

export function klassifiziereFehler(error: unknown, kontext: KlassifikationsKontext): FehlerBefund {
  const code = trpcFehlerCode(error);

  // `null` heisst „kein auswertbarer tRPC-Fehler". Das ist der Netzfall: die
  // Anfrage kam nicht an, oder die Antwort war keine, die der Client auspacken
  // konnte. Ein Link kann diesen Fall NICHT sehen — es gibt keine Antwort, die
  // er auswerten koennte. Genau deshalb steht die Klassifikation hier und nicht
  // als eigener Link in der Kette (Entscheidungsprotokoll E2).
  // `Object.hasOwn` und NICHT nur `ART_NACH_CODE[code]`: Ein Objektliteral erbt
  // von `Object.prototype`, und `code` ist eine Zeichenkette AUS DEM NETZ. Kaeme
  // dort `"toString"` oder `"constructor"` an, lieferte der Zugriff die geerbte
  // Funktion — die ist wahrheitswertig, `??` greift nicht, und `MELDUNGEN[art]`
  // waere `undefined`. Die Oberflaeche zeigte dann gar keine Meldung, und zwar
  // ausgerechnet im Fehlerfall. Nachgestellt, nicht vermutet.
  const bekannt = code !== null && Object.hasOwn(ART_NACH_CODE, code);

  const art: FehlerArt = bestimmeArt(error, code, bekannt);

  return {
    art,
    meldung: MELDUNGEN[art],
    wiederholbar: WIEDERHOLBAR[art],
    neuAnmelden: NEU_ANMELDEN[art],
    // Nur fuer Schreibzugriffe: Eine Abfrage zu wiederholen aendert nichts.
    moeglicherweiseAusgefuehrt: kontext.schreibzugriff && AUSGANG_UNGEWISS[art],
    felder: art === 'eingabe' ? feldBefunde(error) : [],
  };
}
